import React from 'react';
import { Box, Button, Dialog, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import ChatPanel from './ChatPanel';
import { SupportContacts, guidesBase } from '../../support/supportContacts';
import { normalizeLanguage } from '../../../utils/languages';

/**
 * Verstaani avustaja opened large from the corner: the same conversation in a dialog that
 * leaves the current page intact, with customer service and the guides beside it so the
 * user sees the next step when the assistant cannot help.
 */
function ChatLargeDialog({ open, onClose, onShrink, chat, onOpenSupport, onOpenVideos }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = normalizeLanguage(i18n.language);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : 'min(85vh, 820px)',
          borderRadius: fullScreen ? 0 : '16px',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ChatPanel
            variant="large"
            messages={chat.messages}
            loading={chat.loading}
            onSendMessage={chat.sendMessage}
            onSuggestionClick={chat.sendMessage}
            onNewChat={chat.newChat}
            onMinimize={fullScreen ? undefined : onShrink}
            onClose={onClose}
            onOpenSupport={onOpenSupport}
            onOpenVideos={onOpenVideos}
          />
        </Box>

        {/* Beside the chat on a desktop; on a phone the chat's own footer link and the
            escalation button under an unanswered question cover the same ground */}
        <Box
          data-testid="help-chat-large-aside"
          sx={{
            width: 300,
            flexShrink: 0,
            display: { xs: 'none', sm: 'flex' },
            flexDirection: 'column',
            gap: 2,
            p: 2.5,
            borderLeft: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            overflowY: 'auto',
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('helpChatbot.moreHelpTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('helpChatbot.moreHelpLead')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SupportAgentOutlinedIcon />}
            onClick={() => onOpenSupport()}
            data-testid="help-chat-large-support-request"
            sx={{ borderRadius: '10px', textTransform: 'none' }}
          >
            {t('Submit support request')}
          </Button>
          <Button
            variant="outlined"
            href={guidesBase(language)}
            target="_blank"
            rel="noopener"
            endIcon={<OpenInNewIcon />}
            sx={{ borderRadius: '10px', textTransform: 'none' }}
          >
            {t('supportPage.openGuides')}
          </Button>
          <SupportContacts />
          <Link
            component={RouterLink}
            to="/support"
            onClick={onClose}
            variant="body2"
            sx={{ mt: 'auto' }}
          >
            {t('helpChatbot.openSupportPage')}
          </Link>
        </Box>
      </Box>
    </Dialog>
  );
}

export default ChatLargeDialog;
